/**
 * LMS Course Store
 *
 * Lädt den Kurskatalog über die API (/organisation/{orgId}/courses)
 * und verwaltet den Lernfortschritt pro User.
 *
 * Der Fortschritt wird über die Activity-API gespeichert.
 * Die Kurs-ID aus dem Superadmin-Katalog dient dabei als courseId.
 */
import { show, update } from '@training24/course/src/activity'
import { listLessonsForLearner, CourseLesson } from '@training24/course/src/lesson'
import { getEnvironmentVariables } from '@training24/course/src/environmentVariables'
import { toContentUrl } from '@/utils/contentUrl'

export type { CourseLesson }

export interface LmsModule {
    id: string
    title: string
    description: string
    category: string
    duration_minutes: number
    lms_path: string
    provider: string
    image?: string
    published: boolean
}

export interface ScormInteraction {
    id: string
    type?: string
    studentResponse?: string
    result?: string
    correctResponse?: string
    description?: string
    weighting?: number
    latency?: string
    objectives?: string[]
    timestamp?: number
}

export interface ScormState {
    location?: string
    suspendData?: string
    lessonStatus?: string
    successStatus?: string
    scoreRaw?: number
    scoreMin?: number
    scoreMax?: number
    scoreScaled?: number
    progressMeasure?: number
    sessionTime?: string
    totalTime?: string
    interactions?: ScormInteraction[]
    updatedAt?: number
}

/**
 * SCORM-Zeitangabe in Sekunden umrechnen.
 * Unterstützt SCORM 1.2 ("HHHH:MM:SS.ss") und SCORM 2004 (ISO-8601-Dauer "PT1H30M5S").
 */
export function parseScormTime(t?: string): number {
    if (!t) return 0
    // SCORM 2004: ISO-8601-Dauer, z. B. PT1H30M5S oder P1DT2H
    if (/^P/i.test(t)) {
        const m = t.match(/P(?:\d+Y)?(?:\d+M)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:([\d.]+)S)?)?/i)
        if (!m) return 0
        const [, d, h, min, s] = m
        return (parseInt(d || '0', 10) * 86400)
            + (parseInt(h || '0', 10) * 3600)
            + (parseInt(min || '0', 10) * 60)
            + parseFloat(s || '0')
    }
    // SCORM 1.2: HHHH:MM:SS(.ss)
    const parts = t.split(':')
    if (parts.length === 3) {
        return parseInt(parts[0] || '0', 10) * 3600
            + parseInt(parts[1] || '0', 10) * 60
            + parseFloat(parts[2] || '0')
    }
    return 0
}

/** Statuswerte, die „noch nicht begonnen" bedeuten */
const NOT_STARTED_STATUS = ['not attempted', 'not_attempted', 'unknown', '']

export interface LmsProgress {
    moduleId: string
    userId: string
    startedAt: number | null
    completedAt: number | null
    passedSubmodules: { id: string; passedAt: number }[]
    scorm?: Record<string, ScormState>
}

interface LmsCourseState {
    modules: LmsModule[]
    catalogLoaded: boolean
    progress: Record<string, LmsProgress>  // key: moduleId
    lessons: Record<string, CourseLesson[]>  // key: moduleId (courseId)
    lessonsLoaded: Record<string, boolean>
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------
export default {
    namespaced: true,

    state: (): LmsCourseState => ({
        modules: [],
        catalogLoaded: false,
        progress: {},
        lessons: {},
        lessonsLoaded: {},
    }),

    getters: {
        getModules: (state: LmsCourseState) =>
            state.modules.filter(m => m.published),

        getModuleById: (state: LmsCourseState) => (id: string) =>
            state.modules.find(m => m.id === id) ?? null,

        getProgress: (state: LmsCourseState) => (moduleId: string) =>
            state.progress[moduleId] ?? null,

        isCompleted: (state: LmsCourseState) => (moduleId: string) =>
            !!state.progress[moduleId]?.completedAt,

        getLessons: (state: LmsCourseState) => (moduleId: string): CourseLesson[] =>
            state.lessons[moduleId] ?? [],

        isLessonCompleted: (state: LmsCourseState) => (moduleId: string, lessonId: string): boolean => {
            const subs = state.progress[moduleId]?.passedSubmodules ?? []
            return subs.some((s) => s.id === lessonId)
        },

        lessonCompletedAt: (state: LmsCourseState) => (moduleId: string, lessonId: string): number | null => {
            const subs = state.progress[moduleId]?.passedSubmodules ?? []
            const found = subs.find((s) => s.id === lessonId)
            return found ? found.passedAt : null
        },

        completedLessonCount: (state: LmsCourseState) => (moduleId: string): number => {
            return (state.progress[moduleId]?.passedSubmodules ?? []).length
        },

        /**
         * Lektion ist begonnen, aber noch nicht abgeschlossen
         * (Bookmark, suspend_data oder ein Status ungleich „nicht begonnen" vorhanden).
         */
        isLessonInProgress: (state: LmsCourseState) => (moduleId: string, lessonId: string): boolean => {
            const completed = (state.progress[moduleId]?.passedSubmodules ?? []).some((s) => s.id === lessonId)
            if (completed) return false
            const sc = state.progress[moduleId]?.scorm?.[lessonId]
            if (!sc) return false
            return !!(sc.location || sc.suspendData
                || (sc.lessonStatus && !NOT_STARTED_STATUS.includes(sc.lessonStatus)))
        },

        /**
         * Fortschritt einer Lektion als Anteil 0…1 – nur aus echten Daten:
         * abgeschlossen = 1, sonst der vom Paket gemeldete progress_measure.
         * Ohne echten Messwert KEIN geschätzter Wert (kein Vortäuschen von Fortschritt).
         */
        lessonProgress: (state: LmsCourseState) => (moduleId: string, lessonId: string): number => {
            const completed = (state.progress[moduleId]?.passedSubmodules ?? []).some((s) => s.id === lessonId)
            if (completed) return 1
            const sc = state.progress[moduleId]?.scorm?.[lessonId]
            if (sc && typeof sc.progressMeasure === 'number') return Math.max(0, Math.min(1, sc.progressMeasure))
            return 0
        },

        /** Liefert das Paket einen echten Fortschrittswert (progress_measure) für die Lektion? */
        hasLessonMeasure: (state: LmsCourseState) => (moduleId: string, lessonId: string): boolean => {
            const sc = state.progress[moduleId]?.scorm?.[lessonId]
            return !!sc && typeof sc.progressMeasure === 'number'
        },

        /** Anzahl begonnener (aber nicht abgeschlossener) Lektionen */
        inProgressLessonCount: (state: LmsCourseState) => (moduleId: string): number => {
            const lessons = state.lessons[moduleId] ?? []
            const completed = state.progress[moduleId]?.passedSubmodules ?? []
            const scorm = state.progress[moduleId]?.scorm ?? {}
            return lessons.filter((l) => {
                if (completed.some((s) => s.id === l.lessonId)) return false
                const sc = scorm[l.lessonId]
                return !!(sc && (sc.location || sc.suspendData
                    || (sc.lessonStatus && !NOT_STARTED_STATUS.includes(sc.lessonStatus))))
            }).length
        },

        /** Gesamte verbrachte Lernzeit (Summe der SCORM-Zeiten aller Lektionen) in Sekunden */
        totalSpentSeconds: (state: LmsCourseState) => (moduleId: string): number => {
            const scorm = state.progress[moduleId]?.scorm ?? {}
            return Object.values(scorm).reduce((sum, sc) => {
                return sum + (parseScormTime(sc?.totalTime) || parseScormTime(sc?.sessionTime))
            }, 0)
        },

        /** SCORM-Laufzeitstand einer Lektion (für Wiederaufnahme / Bookmark / Score) */
        getScormState: (state: LmsCourseState) => (moduleId: string, lessonId: string): ScormState | null => {
            return state.progress[moduleId]?.scorm?.[lessonId] ?? null
        },

        /**
         * Lektion wurde vom Paket als „nicht bestanden" gemeldet und ist nicht abgeschlossen.
         * SCORM 1.2 meldet das über lesson_status, SCORM 2004 über success_status.
         */
        isLessonFailed: (state: LmsCourseState) => (moduleId: string, lessonId: string): boolean => {
            const completed = (state.progress[moduleId]?.passedSubmodules ?? []).some((s) => s.id === lessonId)
            if (completed) return false
            const sc = state.progress[moduleId]?.scorm?.[lessonId]
            return sc?.lessonStatus === 'failed' || sc?.successStatus === 'failed'
        },
    },

    actions: {
        /** Lädt den Kurskatalog aus der API (/organisation/{orgId}/courses) */
        async LOAD_CATALOG(context: any) {
            const organisationId = context.rootGetters['organisation/getOrganisationId']
            const jwt = context.rootGetters['getJWT']
            // Skip only when catalog was loaded with real credentials AND has results.
            // If a previous call ran without orgId/jwt (too early), retry now.
            if (context.state.catalogLoaded && context.state.modules.length > 0) return
            // Also skip if already loaded and org was available (prevents re-fetch on every watcher tick)
            if (context.state.catalogLoaded && (!jwt || !organisationId)) return

            if (jwt && organisationId) {
                try {
                    const apiPath = `${getEnvironmentVariables().baseDomain}/organisation/${organisationId}/courses`
                    const res = await fetch(apiPath, {
                        headers: { Authorization: `Bearer ${jwt}` },
                    })
                    if (res.ok) {
                        const data = await res.json()
                        const modules: LmsModule[] = data.map((c: any) => ({
                            id: c.courseId,
                            title: c.title,
                            description: c.description || '',
                            category: c.category || '',
                            duration_minutes: c.durationMinutes || 0,
                            lms_path: toContentUrl(c.s3Key || ''),
                            provider: c.provider || 'mindsmith',
                            image: toContentUrl(c.imageUrl || ''),
                            published: c.published,
                        }))
                        context.commit('SET_MODULES', modules)
                        context.commit('SET_CATALOG_LOADED')
                        return
                    }
                } catch {
                    // Netzwerkfehler — leerer Katalog
                }
            }

            context.commit('SET_MODULES', [])
            context.commit('SET_CATALOG_LOADED')
        },

        /** Lektionen für einen Kurs laden */
        async LOAD_LESSONS(context: any, moduleId: string) {
            if (context.state.lessonsLoaded[moduleId]) return
            const organisationId = context.rootGetters['organisation/getOrganisationId']
            const jwt = context.rootGetters['getJWT']
            try {
                const lessons = await listLessonsForLearner(organisationId, moduleId, jwt)
                context.commit('SET_LESSONS', { moduleId, lessons })
            } catch {
                context.commit('SET_LESSONS', { moduleId, lessons: [] })
            }
        },

        /** Lernfortschritt für einen User laden – liest aus der Activity-API */
        async LOAD_PROGRESS(context: any, { userId, moduleId }: { userId: string, moduleId: string }) {
            const organisationId = context.rootGetters['organisation/getOrganisationId']
            const jwt = context.rootGetters['getJWT']
            const year = new Date().getFullYear()

            let completedAt: number | null = null
            let startedAt: number | null = null
            let passedSubmodules: { id: string; passedAt: number }[] = []
            let scorm: Record<string, ScormState> = {}

            try {
                const activity = await show(organisationId, moduleId, year, jwt)
                completedAt = activity?.passedAt ?? null
                startedAt = context.state.progress[moduleId]?.startedAt ?? null
                passedSubmodules = activity?.passedSubmodules ?? []
                scorm = (activity as any)?.scorm ?? {}
            } catch {
                // 404 = noch kein Eintrag → bleibt leer
            }

            context.commit('SET_PROGRESS', { moduleId, userId, startedAt, completedAt, passedSubmodules, scorm })
        },

        /** Kursstart festhalten – nur lokal (startedAt ist kein Activity-API-Feld) */
        async TRACK_STARTED(context: any, { userId, moduleId }: { userId: string, moduleId: string }) {
            const existing = context.state.progress[moduleId]
            if (existing?.startedAt) return   // bereits gestartet

            context.commit('SET_PROGRESS', {
                moduleId,
                userId,
                startedAt: Date.now(),
                completedAt: existing?.completedAt ?? null,
            })
        },

        /** Fortschritt zurücksetzen (passedAt = null) */
        async RESET_PROGRESS(context: any, { userId, moduleId }: { userId: string, moduleId: string }) {
            const organisationId = context.rootGetters['organisation/getOrganisationId']
            const jwt = context.rootGetters['getJWT']
            const year = new Date().getFullYear()

            // SCORM-Stand aller bekannten Lektionen leeren, damit nach dem Reset
            // nicht mehr mittendrin (suspend_data/Bookmark) wieder aufgesetzt wird.
            const existingScorm = context.state.progress[moduleId]?.scorm ?? {}
            const clearedScorm: Record<string, ScormState> = {}
            Object.keys(existingScorm).forEach((k) => { clearedScorm[k] = {} })

            context.commit('SET_PROGRESS', {
                moduleId, userId,
                startedAt: null,
                completedAt: null,
                passedSubmodules: [],
                scorm: clearedScorm,
            })

            try {
                await update(organisationId, moduleId, year, {
                    passedAt: undefined,
                    passedSubmodules: [] as any,
                    showQuestionsInSubmodules: [] as any,
                    ...(Object.keys(clearedScorm).length ? { scorm: clearedScorm } : {}),
                } as any, jwt)
            } catch {
                // Falls noch kein Eintrag existiert, ist nichts zu tun
            }
        },

        /**
         * SCORM-Stand einer einzelnen Lektion zurücksetzen („von vorne beginnen",
         * z. B. nach Nichtbestehen mit aufgebrauchten Versuchen). Leert nur den
         * scorm-Eintrag dieser Lektion – andere Lektionen und der Kursstatus
         * bleiben unberührt (Backend führt scorm je Lektion zusammen).
         */
        async RESET_LESSON_PROGRESS(context: any, { userId, moduleId, lessonId }: { userId: string, moduleId: string, lessonId: string }) {
            const organisationId = context.rootGetters['organisation/getOrganisationId']
            const jwt = context.rootGetters['getJWT']
            const year = new Date().getFullYear()

            const existing = context.state.progress[moduleId]
            context.commit('SET_PROGRESS', {
                moduleId, userId,
                startedAt: existing?.startedAt ?? null,
                completedAt: existing?.completedAt ?? null,
                passedSubmodules: existing?.passedSubmodules ?? [],
                scorm: { ...(existing?.scorm ?? {}), [lessonId]: {} },
            })

            try {
                await update(organisationId, moduleId, year, {
                    scorm: { [lessonId]: {} },
                } as any, jwt)
            } catch {
                // Noch kein Eintrag vorhanden – dann gibt es nichts zurückzusetzen
            }
        },

        /** Einzelne Lektion als abgeschlossen markieren */
        async MARK_LESSON_COMPLETED(context: any, { userId, moduleId, lessonId }: { userId: string, moduleId: string, lessonId: string }) {
            const organisationId = context.rootGetters['organisation/getOrganisationId']
            const jwt = context.rootGetters['getJWT']
            const year = new Date().getFullYear()
            const now = Date.now()

            // Existing submodules, avoid duplicates
            const existing: { id: string; passedAt: number }[] = context.state.progress[moduleId]?.passedSubmodules ?? []
            const alreadyDone = existing.some((s: { id: string }) => s.id === lessonId)
            const passedSubmodules = alreadyDone
                ? existing
                : [...existing, { id: lessonId, passedAt: now }]

            // Check if all lessons are now done
            const allLessons: CourseLesson[] = context.state.lessons[moduleId] ?? []
            const allDone = allLessons.length > 0 && allLessons.every((l) =>
                passedSubmodules.some((s: { id: string }) => s.id === l.lessonId)
            )
            const passedAt = allDone ? now : (context.state.progress[moduleId]?.completedAt ?? null)

            context.commit('SET_PROGRESS', {
                moduleId, userId,
                startedAt: context.state.progress[moduleId]?.startedAt ?? now,
                completedAt: passedAt,
                passedSubmodules,
            })

            await update(organisationId, moduleId, year, {
                passedSubmodules: passedSubmodules as any,
                showQuestionsInSubmodules: [] as any,
                ...(allDone ? { passedAt: now } : {}),
            }, jwt)
        },

        /** Kurs als abgeschlossen markieren – speichert via Activity-API */
        async MARK_COMPLETED(context: any, { userId, moduleId }: { userId: string, moduleId: string }) {
            const passedAt = Date.now()
            const updated: LmsProgress = {
                moduleId,
                userId,
                startedAt: context.state.progress[moduleId]?.startedAt ?? passedAt,
                completedAt: passedAt,
                passedSubmodules: context.state.progress[moduleId]?.passedSubmodules ?? [],
            }

            context.commit('SET_PROGRESS', updated)

            const organisationId = context.rootGetters['organisation/getOrganisationId']
            const jwt = context.rootGetters['getJWT']
            const year = new Date().getFullYear()

            await update(organisationId, moduleId, year, {
                passedAt,
                passedSubmodules: [] as any,
                showQuestionsInSubmodules: [] as any,
            }, jwt)
        },

        /**
         * SCORM-Laufzeitstand einer Lektion speichern (Bookmark, suspend_data, Score, Antworten).
         * Sendet nur das `scorm`-Feld für genau diese Lektion – das Backend führt es mit
         * vorhandenen Daten zusammen, andere Felder (passedSubmodules etc.) bleiben unberührt.
         */
        async SAVE_SCORM_STATE(context: any, { userId, moduleId, lessonId, state }: { userId: string, moduleId: string, lessonId: string, state: ScormState }) {
            const organisationId = context.rootGetters['organisation/getOrganisationId']
            const jwt = context.rootGetters['getJWT']
            const year = new Date().getFullYear()
            const stateWithTs: ScormState = { ...state, updatedAt: Date.now() }

            // Lokalen Stand aktualisieren
            const existing = context.state.progress[moduleId]
            context.commit('SET_PROGRESS', {
                moduleId, userId,
                startedAt: existing?.startedAt ?? null,
                completedAt: existing?.completedAt ?? null,
                passedSubmodules: existing?.passedSubmodules ?? [],
                scorm: { ...(existing?.scorm ?? {}), [lessonId]: stateWithTs },
            })

            try {
                await update(organisationId, moduleId, year, {
                    scorm: { [lessonId]: stateWithTs },
                } as any, jwt)
            } catch {
                // Netzwerkfehler – lokaler Stand bleibt erhalten, nächster Commit versucht es erneut
            }
        },
    },

    mutations: {
        SET_MODULES(state: LmsCourseState, modules: LmsModule[]) {
            state.modules = modules
        },
        SET_CATALOG_LOADED(state: LmsCourseState) {
            state.catalogLoaded = true
        },
        SET_PROGRESS(state: LmsCourseState, progress: LmsProgress) {
            const existing = state.progress[progress.moduleId]
            state.progress = {
                ...state.progress,
                [progress.moduleId]: {
                    ...progress,
                    scorm: progress.scorm ?? existing?.scorm ?? {},
                },
            }
        },
        SET_LESSONS(state: LmsCourseState, { moduleId, lessons }: { moduleId: string; lessons: CourseLesson[] }) {
            state.lessons = { ...state.lessons, [moduleId]: lessons }
            state.lessonsLoaded = { ...state.lessonsLoaded, [moduleId]: true }
        },
    },
}
